import React from 'react';

import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import { useSearchFilters } from 'hooks/useSearchFilters';
import useMartyContext from 'hooks/useMartyContext';
import type { AutoCompleteDataType, FacetDataType } from 'types/search';

interface FacetsSidebarProps {
  facets: FacetDataType[];
  autoComplete: AutoCompleteDataType;
  hasAutoComplete?: boolean;
  onFacetSelect: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
}

export const FacetsSidebar = ({ facets, autoComplete, hasAutoComplete, onFacetSelect }: FacetsSidebarProps) => {
  const { testId } = useMartyContext();

  const { handleFacetSelect, handleAccordionChange, debouncedSearchEvent } = useSearchFilters({
    hasAutoComplete,
    onFacetSelect
  });

  const expandedValues = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);

  return (
    <FacetAccordion.Root type="multiple" value={expandedValues} onValueChange={handleAccordionChange} className="max-md:px-2">
      {facets.map((facet, facetIndex) => {
        const { facetField, facetFieldDisplayName, values } = facet;

        return (
          <FacetAccordion.Item key={facetField} value={facetField} data-test-id={testId('facetGroup')} facet={facet} context="desktop">
            <FacetAccordion.Trigger id={facetField} data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
              <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
            </FacetAccordion.Trigger>

            <FacetAccordion.Content>
              {hasAutoComplete && <FacetAccordion.AutoComplete onChange={e => debouncedSearchEvent(facet, e.target.value)} />}

              <FacetAccordion.ValueList data-test-id={testId(`${facetFieldDisplayName}-values`)} autoComplete={autoComplete}>
                {({ facetValue }) => (
                  <FacetAccordion.Value
                    data-test-id={testId(`facet-${facetValue.name}`)}
                    facetValue={facetValue}
                    onFacetSelect={() => {
                      const valueIndex = values.findIndex(value => value.name === facetValue.name);

                      handleFacetSelect({
                        facetGroup: facetField,
                        facetName: facetValue.name,
                        selectedFacetGroupIndex: facetIndex,
                        selectedFacetIndex: valueIndex,
                        isSelected: facetValue?.selected || false,
                        facetValue
                      });
                    }}
                  />
                )}
              </FacetAccordion.ValueList>
            </FacetAccordion.Content>
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};
